/**
 * Skill 下拉与主输入的衔接：`/` 唤起、打开来源标记、查询同步。
 * 触发态机委托 mentionShared/useMentionTrigger，本文件只做命名适配。
 */
import { useMentionTrigger, type TInputSurface } from '../../mentionShared/useMentionTrigger';

export type TSkillMentionOpenReason = 'button' | 'slash';

export interface IUseSkillMentionHostOptions {
  inputValue: string;
  setInputValue: (value: string) => void;
  textareaRef: React.RefObject<TInputSurface | null>;
}

/** 由 hook 输出、需透传给 SkillMention 的核心状态 */
export interface ISkillMentionHostProps {
  skillDropdownOpen: boolean;
  onSkillDropdownOpenChange: (open: boolean) => void;
  skillMentionQuery: string;
  skillMentionOpenReason: TSkillMentionOpenReason;
  skillMentionAnchorRef: React.RefObject<HTMLElement | null>;
}
export interface IUseSkillMentionHostResult {
  /** true = 已处理，调用方应 return */
  wrapTextareaChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => boolean;
  skillMentionHostProps: ISkillMentionHostProps;
  /** 当前是否处于 / 触发模式且下拉框已打开 */
  isSlashModeOpen: boolean;
  /** / 模式下选中 skill 后，清理输入框中的 /xxx 文本 */
  /** / 模式下选中后清理触发符并可插入引用串 */
  cleanupSlashText: (insertLabel?: string) => void;
  /** 处理输入框获得焦点事件，检查是否需要重新打开 / 下拉框 */
  handleTextareaFocus: () => void;
}

export const useSkillMentionHost = ({
  inputValue,
  setInputValue,
  textareaRef,
}: IUseSkillMentionHostOptions): IUseSkillMentionHostResult => {
  const trigger = useMentionTrigger({
    triggerChar: '/',
    charOpenReason: 'slash',
    inputValue,
    setInputValue,
    textareaRef,
    anchorDataAttr: 'data-skill-mention-anchor',
    // 与历史行为一致：关窗时清 slashIndex
    clearIndexOnClose: true,
  });

  return {
    wrapTextareaChange: trigger.wrapTextareaChange,
    skillMentionHostProps: {
      skillDropdownOpen: trigger.dropdownOpen,
      onSkillDropdownOpenChange: trigger.onDropdownOpenChange,
      skillMentionQuery: trigger.mentionQuery,
      skillMentionOpenReason: trigger.mentionOpenReason,
      skillMentionAnchorRef: trigger.mentionAnchorRef,
    },
    isSlashModeOpen: trigger.isCharModeOpen,
    cleanupSlashText: trigger.cleanupTriggerText,
    handleTextareaFocus: trigger.handleTextareaFocus,
  };
};
